import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Mic, MicOff, Camera, Upload, AlertTriangle, CheckCircle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { AudioRecorder } from "@/utils/audioRecorder";
import {
  InjurySeverity,
  AreaSafetyStatus,
  INJURY_SEVERITY_LABELS,
  AREA_SAFETY_LABELS,
} from "@/types/incident";

type Step = 1 | 2 | 3 | 4 | 5;

interface FormData {
  description: string;
  injury_severity: InjurySeverity | null;
  body_part: string;
  mechanism_of_injury: string;
  immediate_treatment: string;
  location: string;
  incident_datetime: string;
  shift: string;
  area_safety_status: AreaSafetyStatus | null;
  photo_urls: string[];
  voice_transcript: string;
}

const ReportIncident = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [step, setStep] = useState<Step>(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [workerId, setWorkerId] = useState<string | null>(null);
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [siteId, setSiteId] = useState<string | null>(null);
  const [locations, setLocations] = useState<string[]>([]);
  const audioRecorderRef = useRef<AudioRecorder | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [formData, setFormData] = useState<FormData>({
    description: "",
    injury_severity: null,
    body_part: "",
    mechanism_of_injury: "",
    immediate_treatment: "",
    location: "",
    incident_datetime: new Date().toISOString().slice(0, 16),
    shift: "",
    area_safety_status: null,
    photo_urls: [],
    voice_transcript: "",
  });

  useEffect(() => {
    loadUserProfile();
  }, []);

  const loadUserProfile = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      navigate("/signup");
      return;
    }

    const { data: profile } = await supabase
      .from("worker_profiles")
      .select("id, company_id, site_id")
      .eq("user_id", user.id)
      .single();

    if (profile) {
      setWorkerId(profile.id);
      setCompanyId(profile.company_id);
      setSiteId(profile.site_id);

      // Use common locations - can be enhanced later
      setLocations(["Area Produksi", "Gudang", "Workshop", "Kantor", "Parkiran", "Loading Bay", "Area Mesin"]);
    }
  };

  const handleMicClick = async () => {
    if (isRecording) {
      // Stop recording
      if (audioRecorderRef.current) {
        const audioBlob = await audioRecorderRef.current.stop();
        setIsRecording(false);

        // Transcribe
        const reader = new FileReader();
        reader.onloadend = async () => {
          const base64Audio = (reader.result as string).split(",")[1];
          
          try {
            const { data, error } = await supabase.functions.invoke("transcribe-audio", {
              body: { audio: base64Audio },
            });

            if (error) throw error;
            if (data?.text) {
              setFormData(prev => ({
                ...prev,
                description: prev.description + (prev.description ? " " : "") + data.text,
                voice_transcript: data.text,
              }));
            }
          } catch (err) {
            console.error("Transcription error:", err);
            toast({
              title: "Gagal transkripsi",
              description: "Tidak dapat memproses rekaman suara",
              variant: "destructive",
            });
          }
        };
        reader.readAsDataURL(audioBlob);
      }
    } else {
      // Start recording
      audioRecorderRef.current = new AudioRecorder();
      await audioRecorderRef.current.start();
      setIsRecording(true);
    }
  };

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const fileExt = file.name.split(".").pop();
    const fileName = `${Date.now()}-incident.${fileExt}`;
    const filePath = `incidents/${workerId}/${fileName}`;

    const { error: uploadError } = await supabase.storage
      .from("peer-reports")
      .upload(filePath, file);

    if (uploadError) {
      toast({
        title: "Gagal upload foto",
        description: uploadError.message,
        variant: "destructive",
      });
      return;
    }

    const { data: { publicUrl } } = supabase.storage
      .from("peer-reports")
      .getPublicUrl(filePath);

    setFormData(prev => ({
      ...prev,
      photo_urls: [...prev.photo_urls, publicUrl],
    }));

    toast({
      title: "Foto berhasil diupload",
    });
  };

  const handleSubmit = async () => {
    if (!workerId || !companyId || !formData.injury_severity || !formData.area_safety_status) {
      toast({
        title: "Data tidak lengkap",
        description: "Mohon lengkapi semua data yang diperlukan",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const { data, error } = await supabase.functions.invoke("create-incident", {
        body: {
          reported_by: workerId,
          company_id: companyId,
          site_id: siteId,
          location: formData.location,
          description: formData.description,
          incident_datetime: formData.incident_datetime,
          injury_severity: formData.injury_severity,
          body_part: formData.body_part || undefined,
          mechanism_of_injury: formData.mechanism_of_injury || undefined,
          immediate_treatment: formData.immediate_treatment || undefined,
          area_safety_status: formData.area_safety_status,
          shift: formData.shift || undefined,
          photo_urls: formData.photo_urls.length > 0 ? formData.photo_urls : undefined,
          voice_transcript: formData.voice_transcript || undefined,
        },
      });

      if (error) throw error;

      toast({
        title: "Insiden berhasil dilaporkan",
        description: formData.area_safety_status === "still_unsafe" 
          ? "Supervisor telah diberitahu bahwa area masih berbahaya"
          : "Terima kasih atas laporannya",
      });

      navigate("/home");
    } catch (err) {
      console.error("Submit error:", err);
      toast({
        title: "Gagal melaporkan insiden",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const canProceed = () => {
    switch (step) {
      case 1:
        return formData.description.trim().length > 10;
      case 2:
        return formData.injury_severity !== null;
      case 3:
        return formData.location.trim().length > 0;
      case 4:
        return formData.area_safety_status !== null;
      case 5:
        return true;
      default:
        return false;
    }
  };

  const nextStep = () => {
    if (step < 5) setStep((step + 1) as Step);
  };

  const prevStep = () => {
    if (step > 1) setStep((step - 1) as Step);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="bg-destructive text-destructive-foreground px-4 py-3 flex items-center gap-3 sticky top-0 z-10 pt-safe">
        <button onClick={() => navigate(-1)}>
          <ArrowLeft className="h-6 w-6" />
        </button>
        <div className="flex-1">
          <h1 className="text-lg font-bold">Lapor Insiden</h1>
          <p className="text-sm opacity-90">Langkah {step} dari 5</p>
        </div>
        <AlertTriangle className="h-6 w-6" />
      </div>

      {/* Progress bar */}
      <div className="h-1 bg-muted">
        <div 
          className="h-full bg-destructive transition-all duration-300"
          style={{ width: `${(step / 5) * 100}%` }}
        />
      </div>

      <div className="p-4 max-w-lg mx-auto">
        {/* Step 1: What happened */}
        {step === 1 && (
          <Card>
            <CardContent className="p-4 space-y-4">
              <div className="text-center mb-4">
                <h2 className="text-xl font-semibold">Apa yang terjadi?</h2>
                <p className="text-sm text-muted-foreground">Ceritakan kejadiannya dengan detail</p>
              </div>

              <Textarea
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Jelaskan apa yang terjadi..."
                className="min-h-[150px]"
              />

              <div className="flex gap-2">
                <Button
                  variant={isRecording ? "destructive" : "outline"}
                  className="flex-1"
                  onClick={handleMicClick}
                >
                  {isRecording ? <MicOff className="h-4 w-4 mr-2" /> : <Mic className="h-4 w-4 mr-2" />}
                  {isRecording ? "Berhenti" : "Rekam Suara"}
                </Button>

                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Camera className="h-4 w-4 mr-2" />
                  Ambil Foto
                </Button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  className="hidden"
                  onChange={handlePhotoUpload}
                />
              </div>

              {formData.photo_urls.length > 0 && (
                <div className="flex gap-2 flex-wrap">
                  {formData.photo_urls.map((url, i) => (
                    <img
                      key={i}
                      src={url}
                      alt={`Photo ${i + 1}`}
                      className="w-20 h-20 object-cover rounded-lg"
                    />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Step 2: Injury severity */}
        {step === 2 && (
          <Card>
            <CardContent className="p-4 space-y-4">
              <div className="text-center mb-4">
                <h2 className="text-xl font-semibold">Seberapa parah?</h2>
                <p className="text-sm text-muted-foreground">Pilih tingkat keparahan cedera</p>
              </div>

              <div className="space-y-3">
                {(Object.keys(INJURY_SEVERITY_LABELS) as InjurySeverity[]).map((severity) => {
                  const info = INJURY_SEVERITY_LABELS[severity];
                  const isSelected = formData.injury_severity === severity;
                  return (
                    <button
                      key={severity}
                      className={`w-full p-4 rounded-lg border-2 text-left transition-all ${
                        isSelected 
                          ? "border-primary bg-primary/5" 
                          : "border-border hover:border-primary/50"
                      }`}
                      onClick={() => setFormData(prev => ({ ...prev, injury_severity: severity }))}
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">{info.icon}</span>
                        <div>
                          <p className={`font-medium ${info.color}`}>{info.labelId}</p>
                          <p className="text-sm text-muted-foreground">{info.label}</p>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>

              {(formData.injury_severity === "first_aid" || 
                formData.injury_severity === "medical_treatment" || 
                formData.injury_severity === "lost_time") && (
                <div className="space-y-3 pt-4 border-t">
                  <div>
                    <Label>Bagian tubuh yang cedera</Label>
                    <Input
                      value={formData.body_part}
                      onChange={(e) => setFormData(prev => ({ ...prev, body_part: e.target.value }))}
                      placeholder="Contoh: tangan kanan, kaki kiri"
                    />
                  </div>
                  <div>
                    <Label>Mekanisme cedera</Label>
                    <Input
                      value={formData.mechanism_of_injury}
                      onChange={(e) => setFormData(prev => ({ ...prev, mechanism_of_injury: e.target.value }))}
                      placeholder="Contoh: tertimpa, terpotong, terjatuh"
                    />
                  </div>
                  <div>
                    <Label>Pertolongan pertama yang diberikan</Label>
                    <Input
                      value={formData.immediate_treatment}
                      onChange={(e) => setFormData(prev => ({ ...prev, immediate_treatment: e.target.value }))}
                      placeholder="Contoh: dibersihkan dan diperban"
                    />
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Step 3: Location & Time */}
        {step === 3 && (
          <Card>
            <CardContent className="p-4 space-y-4">
              <div className="text-center mb-4">
                <h2 className="text-xl font-semibold">Dimana & Kapan?</h2>
                <p className="text-sm text-muted-foreground">Lokasi dan waktu kejadian</p>
              </div>

              <div className="space-y-4">
                <div>
                  <Label>Lokasi</Label>
                  <Select
                    value={formData.location}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, location: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Pilih lokasi" />
                    </SelectTrigger>
                    <SelectContent>
                      {locations.map((loc) => (
                        <SelectItem key={loc} value={loc}>{loc}</SelectItem>
                      ))}
                      <SelectItem value="__other">Lokasi lain...</SelectItem>
                    </SelectContent>
                  </Select>
                  {formData.location === "__other" && (
                    <Input
                      className="mt-2"
                      value=""
                      onChange={(e) => setFormData(prev => ({ ...prev, location: e.target.value }))}
                      placeholder="Ketik lokasi"
                    />
                  )}
                </div>

                <div>
                  <Label>Waktu Kejadian</Label>
                  <Input
                    type="datetime-local"
                    value={formData.incident_datetime}
                    onChange={(e) => setFormData(prev => ({ ...prev, incident_datetime: e.target.value }))}
                  />
                </div>

                <div>
                  <Label>Shift</Label>
                  <Select
                    value={formData.shift}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, shift: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Pilih shift" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pagi">Shift Pagi</SelectItem>
                      <SelectItem value="siang">Shift Siang</SelectItem>
                      <SelectItem value="malam">Shift Malam</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 4: Area Safety Status */}
        {step === 4 && (
          <Card>
            <CardContent className="p-4 space-y-4">
              <div className="text-center mb-4">
                <h2 className="text-xl font-semibold">Kondisi Area Saat Ini</h2>
                <p className="text-sm text-muted-foreground">Apakah area sudah aman?</p>
              </div>

              <div className="space-y-3">
                {(Object.keys(AREA_SAFETY_LABELS) as AreaSafetyStatus[]).map((status) => {
                  const info = AREA_SAFETY_LABELS[status];
                  const isSelected = formData.area_safety_status === status;
                  return (
                    <button
                      key={status}
                      className={`w-full p-4 rounded-lg border-2 text-left transition-all ${
                        isSelected 
                          ? "border-primary bg-primary/5" 
                          : "border-border hover:border-primary/50"
                      }`}
                      onClick={() => setFormData(prev => ({ ...prev, area_safety_status: status }))}
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">{info.icon}</span>
                        <div>
                          <p className={`font-medium ${info.color}`}>{info.labelId}</p>
                          <p className="text-sm text-muted-foreground">{info.label}</p>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>

              {formData.area_safety_status === "still_unsafe" && (
                <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-4 mt-4">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="h-5 w-5 text-destructive flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-destructive">Supervisor akan segera diberitahu</p>
                      <p className="text-sm text-muted-foreground">
                        Karena area masih berbahaya, supervisor akan menerima notifikasi darurat untuk segera memeriksa lokasi.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Step 5: Confirmation */}
        {step === 5 && (
          <Card>
            <CardContent className="p-4 space-y-4">
              <div className="text-center mb-4">
                <CheckCircle className="h-16 w-16 mx-auto text-primary mb-3" />
                <h2 className="text-xl font-semibold">Konfirmasi Laporan</h2>
                <p className="text-sm text-muted-foreground">Pastikan data sudah benar</p>
              </div>

              <div className="space-y-3 text-sm">
                <div className="flex justify-between py-2 border-b">
                  <span className="text-muted-foreground">Deskripsi</span>
                  <span className="text-right max-w-[60%]">{formData.description.substring(0, 50)}...</span>
                </div>
                <div className="flex justify-between py-2 border-b">
                  <span className="text-muted-foreground">Keparahan</span>
                  <span className={INJURY_SEVERITY_LABELS[formData.injury_severity!]?.color}>
                    {INJURY_SEVERITY_LABELS[formData.injury_severity!]?.labelId}
                  </span>
                </div>
                <div className="flex justify-between py-2 border-b">
                  <span className="text-muted-foreground">Lokasi</span>
                  <span>{formData.location}</span>
                </div>
                <div className="flex justify-between py-2 border-b">
                  <span className="text-muted-foreground">Waktu</span>
                  <span>{new Date(formData.incident_datetime).toLocaleString("id-ID")}</span>
                </div>
                <div className="flex justify-between py-2 border-b">
                  <span className="text-muted-foreground">Status Area</span>
                  <span className={AREA_SAFETY_LABELS[formData.area_safety_status!]?.color}>
                    {AREA_SAFETY_LABELS[formData.area_safety_status!]?.labelId}
                  </span>
                </div>
                {formData.photo_urls.length > 0 && (
                  <div className="flex justify-between py-2 border-b">
                    <span className="text-muted-foreground">Foto</span>
                    <span>{formData.photo_urls.length} foto</span>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Navigation buttons */}
        <div className="flex gap-3 mt-6">
          {step > 1 && (
            <Button variant="outline" className="flex-1" onClick={prevStep}>
              Kembali
            </Button>
          )}
          
          {step < 5 ? (
            <Button 
              className="flex-1" 
              onClick={nextStep}
              disabled={!canProceed()}
            >
              Lanjut
            </Button>
          ) : (
            <Button 
              className="flex-1 bg-destructive hover:bg-destructive/90" 
              onClick={handleSubmit}
              disabled={isSubmitting}
            >
              {isSubmitting ? "Mengirim..." : "Kirim Laporan"}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReportIncident;
